/**
 * @file Reads an `orderLinesTotals` answer into the Orders tab's totals model:
 * open lines and forms, past promised lines with the value at risk, and each
 * lifecycle's line count and value, every one a figure of its own. Consumer:
 * `AnalyticsOrdersPanel`. A value reads the credited column at My sales and My
 * team and its whole `line` twin at Org, by the answer's granted level. An
 * absent or loading answer loads every figure and a refused or failed one
 * refuses every figure; an answer that is not exactly one row fails every
 * figure, a refused column is its refusal, and a count cell that is not a
 * whole number fails that figure alone, so no figure ever reads zero instead.
 */
import type { Snapshot } from 'valtio';

import { analyticsAnswerFigure } from './analyticsAnswerFigure';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type { AnalyticsOrderStatuses } from './analyticsOrdersTab.types';
import type {
  AnalyticsAnswer,
  AnalyticsCountFigure,
  AnalyticsDecimalFigure,
  AnalyticsOrderLifecycle,
} from './index.types';

type ValueColumns = { credited: string; whole: string };

const LIFECYCLE_COLUMNS: Record<
  AnalyticsOrderLifecycle,
  { lines: string; value: ValueColumns }
> = {
  held: {
    lines: 'heldLines',
    value: { credited: 'heldValue', whole: 'lineHeldValue' },
  },
  released: {
    lines: 'releasedLines',
    value: { credited: 'releasedValue', whole: 'lineReleasedValue' },
  },
  shipped: {
    lines: 'shippedLines',
    value: { credited: 'shippedValue', whole: 'lineShippedValue' },
  },
};

const AT_RISK: ValueColumns = {
  credited: 'valueAtRisk',
  whole: 'lineValueAtRisk',
};

const countFigure = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  column: string,
): AnalyticsCountFigure => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const refused = analyticsRefusedColumn(answer.result, column);
  if (refused !== null) return { status: 'refusedColumn', column: refused };
  const cell = answer.result.rows[0]?.[column];
  return typeof cell === 'number' && Number.isInteger(cell)
    ? { status: 'answered', count: cell }
    : analyticsMalformedAnswer('orderLinesTotals');
};

export const analyticsOrderStatuses = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsOrderStatuses => {
  const isOneRow =
    answer?.status !== 'answered' || answer.result.rows.length === 1;
  const isWhole =
    answer?.status === 'answered' && answer.result.level === 'org';
  const count = (column: string): AnalyticsCountFigure =>
    isOneRow
      ? countFigure(answer, column)
      : analyticsMalformedAnswer('orderLinesTotals');
  const value = ({ credited, whole }: ValueColumns): AnalyticsDecimalFigure =>
    isOneRow
      ? analyticsAnswerFigure(answer, isWhole ? whole : credited)
      : analyticsMalformedAnswer('orderLinesTotals');
  const lifecycle = (key: AnalyticsOrderLifecycle) => ({
    lines: count(LIFECYCLE_COLUMNS[key].lines),
    value: value(LIFECYCLE_COLUMNS[key].value),
  });
  return {
    openLines: count('openLines'),
    openForms: count('openForms'),
    pastPromisedLines: count('pastPromisedLines'),
    valueAtRisk: value(AT_RISK),
    lifecycle: {
      held: lifecycle('held'),
      released: lifecycle('released'),
      shipped: lifecycle('shipped'),
    },
  };
};
